import { useCallback, useSyncExternalStore } from 'react';
import { getStoredFunMode, subscribeFunMode } from './funMode';
import { MESSAGES } from './catalog';

type Params = Record<string, string | number>;

const INTERP = /\{(\w+)\}/g;

function interpolate(text: string, params?: Params): string {
  if (!params) return text;
  return text.replace(INTERP, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/**
 * Resolve a message key to text for the given mode, interpolating `{param}`
 * tokens. Missing key → the key itself (dev-warns), so a typo is visible rather
 * than blank. Pure — also used off the React tree and in tests.
 */
export function translate(key: string, on: boolean, params?: Params): string {
  const entry = MESSAGES[key];
  if (!entry) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing message key: ${key}`);
    }
    return interpolate(key, params);
  }
  return interpolate(on ? entry.fun : entry.en, params);
}

/** Reactive fun-mode flag; re-renders the caller when the toggle flips. */
export function useFunMode(): boolean {
  return useSyncExternalStore(subscribeFunMode, getStoredFunMode, () => false);
}

/** The translator hook: `const t = useT(); t('settings.profile.title')`. */
export function useT(): (key: string, params?: Params) => string {
  const on = useFunMode();
  return useCallback(
    (key: string, params?: Params) => translate(key, on, params),
    [on],
  );
}
